/**
 * Компонент выбора документов из базы знаний для AI чата
 */

import { useList } from '@refinedev/core';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  FileText,
  Loader2,
  AlertCircle,
  Upload,
  Check,
  Clock,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { KnowledgeDocument } from './types';

interface KnowledgeBaseSelectorProps {
  selectedDocuments: number[];
  onSelectedDocumentsChange: (ids: number[]) => void;
  onUploadClick?: () => void;
  className?: string;
}

/**
 * Форматирование размера файла
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) {return `${bytes} B`;}
  if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Иконка статуса документа
 */
function StatusIcon({ status }: { status: KnowledgeDocument['status'] }) {
  switch (status) {
    case 'COMPLETED':
      return <Check className="h-3 w-3" />;
    case 'PROCESSING':
      return <Loader2 className="h-3 w-3 animate-spin" />;
    case 'PENDING':
      return <Clock className="h-3 w-3 text-muted-foreground" />;
    case 'FAILED':
      return <XCircle className="h-3 w-3" />;
    default:
      return null;
  }
}

/**
 * Элемент документа
 */
function DocumentItem({
  document,
  isSelected,
  onToggle,
}: {
  document: KnowledgeDocument;
  isSelected: boolean;
  onToggle: () => void;
}) {
  const isAvailable = document.status === 'COMPLETED';

  return (
    <label
      className={cn(
        'flex items-start gap-2 p-2 rounded-md cursor-pointer',
        'hover:bg-muted/50 transition-colors',
        !isAvailable && 'opacity-50 cursor-not-allowed'
      )}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={onToggle}
        disabled={!isAvailable}
        className="mt-0.5"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <span className="text-xs font-medium truncate">{document.filename}</span>
          <StatusIcon status={document.status} />
        </div>

        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
          <span>{formatFileSize(document.fileSize)}</span>
          {document.status === 'COMPLETED' && (
            <span>{document.chunksCount} чанков</span>
          )}
          {document.status === 'PROCESSING' && <span>Обработка...</span>}
          {document.status === 'FAILED' && (
            <span className="text-destructive">Ошибка</span>
          )}
        </div>
      </div>
    </label>
  );
}

/**
 * Главный компонент выбора документов
 */
export function KnowledgeBaseSelector({
  selectedDocuments,
  onSelectedDocumentsChange,
  onUploadClick,
  className,
}: KnowledgeBaseSelectorProps) {
  // Используем стандартный useList для ресурса knowledge-documents
  const listResult = useList<KnowledgeDocument>({
    resource: 'knowledge-documents',
    pagination: { mode: 'off' }, // Загружаем все документы
    queryOptions: {
      staleTime: 30000, // 30 секунд
    },
  });

  const documents = listResult.result?.data ?? [];
  const isLoading = listResult.query?.isLoading ?? false;
  const isError = listResult.query?.isError ?? false;
  const refetch = listResult.query?.refetch;

  const handleToggle = (docId: number) => {
    if (selectedDocuments.includes(docId)) {
      onSelectedDocumentsChange(selectedDocuments.filter((id) => id !== docId));
    } else {
      onSelectedDocumentsChange([...selectedDocuments, docId]);
    }
  };

  const handleSelectAll = () => {
    const availableIds = documents
      .filter((d) => d.status === 'COMPLETED')
      .map((d) => d.id);
    onSelectedDocumentsChange(availableIds);
  };

  const handleDeselectAll = () => {
    onSelectedDocumentsChange([]);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {/* Действия */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {selectedDocuments.length > 0 ? (
            <button
              type="button"
              onClick={handleDeselectAll}
              className="text-[10px] text-muted-foreground hover:text-foreground"
            >
              Снять выбор
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-[10px] text-muted-foreground hover:text-foreground"
            >
              Выбрать все
            </button>
          )}
        </div>

        {selectedDocuments.length > 0 && (
          <Badge variant="secondary" className="text-[10px]">
            {selectedDocuments.length} выбрано
          </Badge>
        )}
      </div>

      {/* Список документов */}
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <span className="text-xs text-muted-foreground">
            Ошибка загрузки документов
          </span>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            Повторить
          </Button>
        </div>
      ) : documents.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            Нет загруженных документов
          </span>
          {onUploadClick && (
            <Button variant="outline" size="sm" onClick={onUploadClick}>
              <Upload className="h-3 w-3 mr-1" />
              Загрузить
            </Button>
          )}
        </div>
      ) : (
        <ScrollArea className="max-h-48">
          <div className="space-y-0.5">
            {documents.map((doc) => (
              <DocumentItem
                key={doc.id}
                document={doc}
                isSelected={selectedDocuments.includes(doc.id)}
                onToggle={() => handleToggle(doc.id)}
              />
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Кнопка загрузки */}
      {documents.length > 0 && onUploadClick && (
        <Button
          variant="outline"
          size="sm"
          onClick={onUploadClick}
          className="w-full text-xs"
        >
          <Upload className="h-3 w-3 mr-1" />
          Загрузить документ
        </Button>
      )}
    </div>
  );
}
